'use client';
import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _extends } from "@swc/helpers/_/_extends";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@kamio/ui/components/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@kamio/ui/components/form';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, REGEXP_ONLY_DIGITS } from '@kamio/ui/components/input-otp';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { PhoneInput } from '../inputs/phone-input';
import { LoadingButton } from '../loading-button';
// Duplication due to nextjs issue, cannot export objects from server functions...
const phoneSchema = z.object({
    phone: z.string().refine(isValidPhoneNumber, 'Invalid phone number'),
    sent: z.boolean().optional(),
    otp: z.string().min(6, {
        message: 'OTP must be 6 characters'
    }).optional()
}).superRefine((data, ctx)=>{
    if (data.sent && !data.otp) {
        ctx.addIssue({
            path: [
                'otp'
            ],
            code: 'custom',
            message: 'OTP is required',
            validation: 'email'
        });
    }
});
export function OtpForm({ confirmOtp: confirm, requestOtp: fetchOtp, pathAfterSignIn = '/' }) {
    'use client';
    const router = useRouter();
    const [resendTimeout, setResendTimeout] = useState(0);
    const form = useForm({
        resolver: zodResolver(phoneSchema),
        mode: 'onChange',
        defaultValues: {
            sent: false
        }
    });
    const startResendTimeout = ()=>{
        setResendTimeout(30);
        const timer = setInterval(()=>{
            setResendTimeout((prevTimeout)=>{
                if (prevTimeout <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prevTimeout - 1;
            });
        }, 1000);
    };
    function requestOtp(phone) {
        return _async_to_generator(function*() {
            const q = parsePhoneNumber(phone);
            const result = yield fetchOtp({
                phone: q.number
            });
            console.log(result);
            if (result == null ? void 0 : result.error) {
                throw new Error(result.error);
            }
            startResendTimeout();
            form.setValue('sent', true);
            toast.success('An OTP was sent to your phone.');
            console.log('sent');
        })();
    }
    function confirmOtp(data) {
        return _async_to_generator(function*() {
            const result = yield confirm({
                phone: data.phone,
                otp: data.otp
            });
            if (result.error) {
                throw new Error(result.error);
            }
            toast.success('Signed In Sucessfully.');
            router.push(pathAfterSignIn);
        })();
    }
    function onSubmit(data) {
        return _async_to_generator(function*() {
            try {
                if (data.sent) {
                    yield confirmOtp({
                        phone: data.phone,
                        otp: data.otp
                    });
                } else {
                    yield requestOtp(data.phone);
                }
            } catch (ex) {
                if (ex instanceof Error) {
                    console.error(ex);
                    toast.error(`An Error Occured: ${ex.message}`);
                }
            }
        })();
    }
    const sent = form.watch('sent');
    return /*#__PURE__*/ _jsx(Form, _extends({}, form, {
        children: /*#__PURE__*/ _jsx("form", {
            onSubmit: form.handleSubmit(onSubmit),
            className: "animate-in",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(FormField, {
                        control: form.control,
                        name: "phone",
                        render: ({ field })=>/*#__PURE__*/ _jsxs(FormItem, {
                                children: [
                                    /*#__PURE__*/ _jsx(FormLabel, {
                                        children: "Mobile Number"
                                    }),
                                    /*#__PURE__*/ _jsx(FormControl, {
                                        children: /*#__PURE__*/ _jsxs("div", {
                                            className: "flex gap-1",
                                            children: [
                                                /*#__PURE__*/ _jsx(PhoneInput, _extends({}, field, {
                                                    autoComplete: "mobile tel",
                                                    placeholder: "+1234567890"
                                                })),
                                                sent && /*#__PURE__*/ _jsxs(Button, {
                                                    type: "button",
                                                    className: "my-auto",
                                                    size: "sm",
                                                    disabled: resendTimeout > 0,
                                                    onClick: ()=>{
                                                        requestOtp(form.getValues('phone'));
                                                    },
                                                    children: [
                                                        "Resend",
                                                        ' ',
                                                        resendTimeout > 0 ? `(${resendTimeout})` : ''
                                                    ]
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(FormMessage, {})
                                ]
                            })
                    }),
                    sent && /*#__PURE__*/ _jsx(FormField, {
                        control: form.control,
                        name: "otp",
                        render: ({ field })=>/*#__PURE__*/ _jsxs(FormItem, {
                                children: [
                                    /*#__PURE__*/ _jsx(FormLabel, {
                                        children: "OTP"
                                    }),
                                    /*#__PURE__*/ _jsx(FormControl, {
                                        children: /*#__PURE__*/ _jsx(InputOTP, {
                                            className: "mx-auto",
                                            name: field.name,
                                            maxLength: 6,
                                            pattern: REGEXP_ONLY_DIGITS,
                                            // defaultValue={field.value}
                                            value: field.value,
                                            onChange: (v)=>{
                                                field.onChange(v);
                                            },
                                            children: /*#__PURE__*/ _jsxs(InputOTPGroup, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(InputOTPSlot, {
                                                        index: 0
                                                    }),
                                                    /*#__PURE__*/ _jsx(InputOTPSlot, {
                                                        index: 1
                                                    }),
                                                    /*#__PURE__*/ _jsx(InputOTPSlot, {
                                                        index: 2
                                                    }),
                                                    /*#__PURE__*/ _jsx(InputOTPSeparator, {}),
                                                    /*#__PURE__*/ _jsx(InputOTPSlot, {
                                                        index: 3
                                                    }),
                                                    /*#__PURE__*/ _jsx(InputOTPSlot, {
                                                        index: 4
                                                    }),
                                                    /*#__PURE__*/ _jsx(InputOTPSlot, {
                                                        index: 5
                                                    })
                                                ]
                                            })
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(FormMessage, {})
                                ]
                            })
                    }),
                    /*#__PURE__*/ _jsx(LoadingButton, {
                        className: "w-full",
                        type: "submit",
                        isLoading: form.formState.isSubmitting,
                        children: sent ? 'Confirm OTP' : 'Send OTP'
                    })
                ]
            })
        })
    }));
}
